import { toast } from "sonner";
import type { ToasterProps } from "sonner";
import type { ReactElement, ReactNode } from "react";

type ToastPosition = ToasterProps["position"];

/**
 * **Single source of truth pour les notifications de l'application.**
 *
 * À utiliser partout au lieu de `import { toast } from "sonner"`.
 * Toutes les options par défaut (durée, style) sont centralisées ici pour garantir
 * un comportement uniforme — modifier ce fichier suffit à mettre à jour toute l'app.
 *
 * Le `<Toaster>` global est configuré dans `src/main.tsx` (position, richColors).
 *
 * @example
 * import { notify } from "@/lib/notify";
 * notify.success("Niveau créé");
 * notify.error("Échec de la mise à jour", { description: error.message });
 */

const DEFAULT_DURATION = 4000;

type BaseOpts = { description?: ReactNode; duration?: number; position?: ToastPosition };

export const notify = {
  success(message: string, opts?: BaseOpts) {
    return toast.success(message, {
      description: opts?.description,
      duration: opts?.duration ?? DEFAULT_DURATION,
      position: opts?.position,
    });
  },

  error(message: string, opts?: BaseOpts) {
    return toast.error(message, {
      description: opts?.description,
      duration: opts?.duration ?? DEFAULT_DURATION + 1000,
      position: opts?.position,
    });
  },

  warning(message: string, opts?: BaseOpts) {
    return toast.warning(message, {
      description: opts?.description,
      duration: opts?.duration ?? DEFAULT_DURATION,
      position: opts?.position,
    });
  },

  info(message: string, opts?: BaseOpts) {
    return toast.info(message, {
      description: opts?.description,
      duration: opts?.duration ?? DEFAULT_DURATION,
      position: opts?.position,
    });
  },

  loading(message: string) {
    return toast.loading(message);
  },

  dismiss(id?: string | number) {
    return toast.dismiss(id);
  },

  /**
   * Wrap une promise et affiche loading → success/error automatiquement.
   */
  promise<T>(promise: Promise<T>, messages: { loading: string; success: string; error: string }) {
    return toast.promise(promise, messages);
  },

  /**
   * Toast brut sans variante (équivalent de `toast(message)`).
   */
  show(message: string, opts?: BaseOpts) {
    return toast(message, {
      description: opts?.description,
      duration: opts?.duration ?? DEFAULT_DURATION,
      position: opts?.position,
    });
  },

  /**
   * Toast custom rendu via un composant React (équivalent de `toast.custom`).
   */
  custom(render: (id: string | number) => ReactElement, opts?: { duration?: number }) {
    return toast.custom(render, { duration: opts?.duration });
  },
};
